// Re-use the single Axios instance from lib/api.ts so that ALL requests
// share the same token-refresh queue / dedup logic.  Having a second
// Axios instance with its own 401 interceptor caused race conditions
// (concurrent, uncoordinated refresh calls) that logged users out.
import { api } from '@/lib/api'

export const apiClient = api

// Deal Positions API
export interface DealPosition {
  _id: string
  orgId: string
  pipelineId: string
  stageId: string
  dealId: string
  rank: string
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
}

/**
 * One Kanban card. Mirrors `BoardDeal` on the API: only the fields the card draws,
 * plus the rank and stage from its board position. Anything a modal needs beyond this
 * is fetched per deal when the modal opens.
 */
export interface BoardDeal {
  _id: string
  name: string
  value?: number
  currency?: string
  status?: string
  priority?: string
  source?: string
  tags?: string[]
  company?: string
  companyId?: { _id: string; name?: string } | string
  contactName?: string
  contactId?: string
  assignedTo?: { _id: string; name?: string } | string
  lastActivityAt?: string
  createdAt?: string
  pipelineId?: string
  stageId?: string
  rank: string
  /** AI recommendation for the deal's contact, when its calls have been analysed. */
  leadQualification?: import('@/stores/deals').DealLeadQualification
  /** Earliest-due open activity on the deal; `null` when there is none. */
  nextStep?: import('@/stores/deals').DealNextStep | null
  /** Most recent recurring line item (product and tier); `null` when the deal has none. */
  plan?: import('@/stores/deals').DealPlan | null
  /**
   * The deal keeps a `contactId` whose contact was deleted or merged away (as on GET /deals/:id).
   * Absent from an API older than the field: read it as false.
   */
  contactMissing?: boolean
}

export interface MoveDealDto {
  dealId: string
  toStageId: string
  afterDealId?: string | undefined
  beforeDealId?: string | undefined
  toBottom?: boolean
}

export interface MoveResult {
  success: boolean
  position: DealPosition
  stageChanged?: boolean
}

// Profile API
export interface UpdateProfileDto {
  name?: string
}

export interface ChangePasswordDto {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface UserPreferences {
  activityReminderMinutes: number
  emailNotificationsEnabled: boolean
  browserNotificationsEnabled: boolean
  inAppNotificationsEnabled: boolean
}

export interface UpdatePreferencesDto {
  activityReminderMinutes?: number
  emailNotificationsEnabled?: boolean
  browserNotificationsEnabled?: boolean
  inAppNotificationsEnabled?: boolean
}

export interface ProfileResponse {
  _id: string
  email: string
  name: string
  role: string
  status: string
  orgId: string
  avatar?: string
  preferences?: UserPreferences
  createdAt: string
  updatedAt: string
}

export const profileApi = {
  updateProfile: async (data: UpdateProfileDto): Promise<ProfileResponse> => {
    const response = await apiClient.patch('/auth/profile', data)
    return response.data.data
  },

  changePassword: async (data: ChangePasswordDto): Promise<{ message: string }> => {
    const response = await apiClient.patch('/auth/profile/password', data)
    return response.data
  },

  uploadAvatar: async (file: File): Promise<ProfileResponse> => {
    const formData = new FormData()
    formData.append('avatar', file)
    const response = await apiClient.post('/auth/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  },

  removeAvatar: async (): Promise<ProfileResponse> => {
    const response = await apiClient.delete('/auth/profile/avatar')
    return response.data.data
  },

  getPreferences: async (): Promise<UserPreferences> => {
    const response = await apiClient.get('/auth/profile/preferences')
    return response.data.data
  },

  updatePreferences: async (data: UpdatePreferencesDto): Promise<UserPreferences> => {
    const response = await apiClient.patch('/auth/profile/preferences', data)
    return response.data.data
  },
}

export interface BoardStagePage {
  stageId: string
  /** Every card in this column that matches the filters, not just the loaded ones. */
  total: number
  cards: BoardDeal[]
  hasMore: boolean
}

/** A column of `getBoard`: its first page plus the column's value sum. */
export interface BoardColumn extends BoardStagePage {
  /**
   * Sum of the deal values in the column under the same filters as `total` (every matching
   * deal, not only the loaded cards). Absent from an API older than the field: read it as 0.
   */
  valueSum?: number
}

export interface BoardPage {
  pageSize: number
  stages: BoardColumn[]
}

export interface BoardFilters {
  assignedTo?: string
  search?: string
  lastActivityAfter?: string
  lastActivityBefore?: string
  assignedAfter?: string
  assignedBefore?: string
  /** Repeated ?tag=a&tag=b — deals carrying ANY of these tags. */
  tag?: string[]
  /** AI recommendation of the deal's contact: qualified, nurture, in_progress, disqualified. */
  leadStatus?: string
}

export const dealPositionsApi = {
  /**
   * Everything the board renders in one call: the first page of each column's lean
   * deal cards, already carrying their rank and stage, plus a true per-column total.
   * Replaces getPositions + a full deals fetch, which shipped ~7.8 MB on a large
   * pipeline and timed the client out.
   */
  getBoard: async (
    pipelineId: string,
    /** `cards: false`: the totals and value sums only, every column's cards empty (the phone board). */
    params?: BoardFilters & { cards?: boolean },
  ): Promise<BoardPage> => {
    const response = await apiClient.get(`/pipelines/${pipelineId}/board`, { params })
    return response.data.data
  },

  /**
   * One more page of a single board column, past the first page `getBoard` already sent.
   */
  getStagePage: async (
    pipelineId: string,
    stageId: string,
    params: BoardFilters & { page: number; sortBy?: string; sortOrder?: 'asc' | 'desc' },
  ): Promise<BoardStagePage> => {
    const response = await apiClient.get(`/pipelines/${pipelineId}/board/stages/${stageId}`, { params })
    return response.data.data
  },

  /**
   * Move a deal to a new position (drag-and-drop)
   */
  moveDeal: async (pipelineId: string, moveDto: MoveDealDto): Promise<MoveResult> => {
    const response = await apiClient.post(`/pipelines/${pipelineId}/positions/move`, moveDto)
    return response.data
  },
}